import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useApp } from "@/context/AppContext";
import { TESTIMONIALS } from "@/lib/testimonials";
import { Star, ChevronLeft, ChevronRight, Quote, Info } from "lucide-react";

const AVATAR_COLORS = ["#7C3AED", "#F59E0B", "#22D3EE", "#F472B6", "#34D399", "#C084FC"];

function Stars() {
  return (
    <div className="flex gap-0.5" aria-label="5 star rating">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={15} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const { t, lang } = useApp();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const [canScroll, setCanScroll] = useState(true);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setCanScroll(true);
    const id = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 4000);
    return () => clearInterval(id);
  }, [emblaApi]);

  return (
    <section className="mx-auto max-w-7xl px-6 py-16" data-testid="testimonials-section">
      <div className="text-center mb-10">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-amber-100">{t.testimonials.title}</h2>
        <p className="mt-3 text-slate-300 max-w-xl mx-auto">{t.testimonials.subtitle}</p>
      </div>

      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-5 py-2">
            {TESTIMONIALS.map((r) => (
              <div
                key={r.id}
                className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                data-testid={`testimonial-${r.id}`}
              >
                <div className="glass rounded-2xl p-6 h-full flex flex-col">
                  <Quote className="text-amber-400/40 mb-3" size={26} />
                  <p className="text-slate-200 leading-relaxed flex-1">{lang === "hi" ? r.hi : r.en}</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div
                      className="h-11 w-11 rounded-full grid place-items-center font-serif text-lg text-white shrink-0"
                      style={{ background: AVATAR_COLORS[r.id % AVATAR_COLORS.length] }}
                    >
                      {r.name[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-amber-100 truncate">{r.name}</div>
                      <div className="text-xs text-slate-400 truncate">{r.city}</div>
                    </div>
                    <div className="ml-auto"><Stars /></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {canScroll && (
          <>
            <button
              onClick={scrollPrev}
              data-testid="testimonial-prev"
              aria-label="Previous"
              className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full glass-strong grid place-items-center text-amber-200 hover:gold-glow transition-all"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={scrollNext}
              data-testid="testimonial-next"
              aria-label="Next"
              className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full glass-strong grid place-items-center text-amber-200 hover:gold-glow transition-all"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      <p className="mt-8 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5" data-testid="testimonials-demo-label">
        <Info size={13} /> {t.testimonials.demoLabel}
      </p>
    </section>
  );
}
